import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { PrismConfigApiItem } from "../types/preferences_types";
import { toast } from "@/shared/hooks/useToast";

// API 함수들
async function fetchPrismConfig(): Promise<PrismConfigApiItem[]> {
  return api.get<PrismConfigApiItem[]>("/prism-config/");
}

async function updatePrismConfigItem(item: PrismConfigApiItem): Promise<void> {
  return api.patch<void>("/prism-config/", item);
}

// Query Hook
export function usePrismConfigQuery() {
  return useQuery({
    queryKey: queryKeys.prismConfig.list(),
    queryFn: fetchPrismConfig,
  });
}

// Mutation Hook
export function useUpdatePrismConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePrismConfigItem,
    onMutate: async (newConfig) => {
      // 진행 중인 쿼리 취소
      await queryClient.cancelQueries({ queryKey: queryKeys.prismConfig.list() });

      // 이전 데이터 백업
      const previousConfig = queryClient.getQueryData<PrismConfigApiItem[]>(queryKeys.prismConfig.list());

      // 낙관적 업데이트: 함수형 업데이터를 사용하여 안전하게 갱신
      queryClient.setQueryData<PrismConfigApiItem[]>(
        queryKeys.prismConfig.list(),
        (oldData) => {
          if (!oldData) return oldData;
          
          return oldData.map((item) => {
            if (item.name === newConfig.name) {
              return { ...item, value: newConfig.value };
            }
            return item;
          });
        }
      );

      // 롤백을 위해 이전 데이터 반환
      return { previousConfig };
    },
    onSuccess: () => {
      // 서버 데이터와 동기화
      queryClient.invalidateQueries({ queryKey: queryKeys.prismConfig.list() });
      toast({
        title: "성공",
        description: "설정이 성공적으로 변경되었습니다.",
        variant: "success",
      });
    },
    onError: (error: Error, _newConfig, context) => {
      // 에러 발생 시 이전 데이터로 롤백
      if (context?.previousConfig) {
        queryClient.setQueryData(queryKeys.prismConfig.list(), context.previousConfig);
      }
      toast({
        title: "오류",
        description: error.message || "설정 변경에 실패했습니다.",
        variant: "destructive",
      });
    },
  });
}
